import React, { useEffect, useState, useCallback } from "react"; // 🔥 useMemo hata diya
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useWebData } from "../context/WebDataContext"; 
import { db } from "../firebaseConfig"; 
import { collection, query, where, getDocs } from "firebase/firestore";
import Loader from "../components/Loader";

// Fix Icons
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

// ✅ ICONS defined OUTSIDE component (Best Performance)
const BlueIcon = L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const RedIcon = L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const PurpleIcon = L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-violet.png',
    shadowUrl: iconShadow,
    iconSize: [20, 32], 
    iconAnchor: [10, 32],
    popupAnchor: [1, -28],
    shadowSize: [32, 32]
});

function MapView() {
  const { attendance, salesReports, loading: contextLoading } = useWebData();

  const [loading, setLoading] = useState(true);
  const [markers, setMarkers] = useState([]);
  const [filterDate, setFilterDate] = useState(new Date().toISOString().slice(0, 10));

  // ✅ useCallback for fetchLocations
  const fetchLocations = useCallback(async () => {
    setLoading(true);
    let points = [];

    try {
        // -------------------------------------------
        // 1. ATTENDANCE (In/Out)
        // -------------------------------------------
        if (attendance) {
            attendance.forEach(d => {
                if (d.date === filterDate) {
                    // 🔵 DAY IN
                    if (d.location && d.location.latitude) {
                        points.push({
                            id: d.id + "_in",
                            lat: d.location.latitude,
                            lng: d.location.longitude,
                            name: d.userName,
                            type: "🌅 Punch In",
                            time: d.inTime,
                            address: d.location.address || "Unknown",
                            icon: BlueIcon
                        });
                    }
                    // 🔴 DAY OUT
                    if (d.outLocation && d.outLocation.latitude) {
                        points.push({
                            id: d.id + "_out",
                            lat: d.outLocation.latitude,
                            lng: d.outLocation.longitude,
                            name: d.userName,
                            type: "🌇 Punch Out",
                            time: d.outTime,
                            address: d.outAddress || "Unknown",
                            icon: RedIcon
                        });
                    }
                }
            });
        }

        // -------------------------------------------
        // 2. SALES VISITS
        // -------------------------------------------
        if (salesReports) {
            salesReports.forEach(d => {
                const visitDate = d.createdAt ? d.createdAt.split('T')[0] : '';
                if (visitDate === filterDate && d.latitude && d.longitude) {
                    points.push({
                        id: d.id,
                        lat: d.latitude,
                        lng: d.longitude,
                        name: d.userName,
                        type: `🤝 Visit: ${d.hospital}`,
                        time: new Date(d.createdAt).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}),
                        address: d.city || "",
                        icon: RedIcon
                    });
                }
            });
        }

        // -------------------------------------------
        // 3. AUTO LOGS (30-min Tracker)
        // -------------------------------------------
        try {
            const logsRef = collection(db, "location_logs");
            const q = query(logsRef, where("date", "==", filterDate));
            const querySnapshot = await getDocs(q);

            querySnapshot.forEach((doc) => {
                const d = doc.data();
                if (d.latitude && d.longitude) {
                    points.push({
                        id: doc.id,
                        lat: d.latitude,
                        lng: d.longitude,
                        name: d.userName,
                        type: "🟣 Auto-Track (30min)",
                        time: d.time || "Unknown",
                        address: "En-route",
                        icon: PurpleIcon
                    });
                }
            });
        } catch (err) {
            console.log("No auto logs found yet or error:", err);
        }

        console.log("📍 Total Markers Found:", points.length);
        setMarkers(points);

    } catch (error) {
        console.error("Error fetching map data:", error);
    }
    setLoading(false);
  }, [attendance, salesReports, filterDate]);


  // ✅ useEffect
  useEffect(() => {
    if(!contextLoading) {
        fetchLocations();
    }
  }, [fetchLocations, contextLoading]);


  return (
    <div style={{height: "100vh", display: "flex", flexDirection: "column"}}>
      
      {/* HEADER */}
      <div style={headerStyle}>
          <h2 style={{margin:0, color:"#2c3e50"}}>🗺️ Live Tracker</h2>
          <div style={{display:"flex", gap:"10px", alignItems:"center"}}>
              <label style={{fontWeight:'bold'}}>Date:</label>
              <input type="date" value={filterDate} onChange={(e)=>setFilterDate(e.target.value)} style={inputStyle} />
              <button onClick={fetchLocations} style={btnRefresh}>🔄 Refresh Map</button>
          </div>
      </div>

      {/* MAP */}
      <div style={{flex:1, position:"relative"}}>
          {(loading || contextLoading) && <div style={loaderOverlay}><Loader /></div>}
          
          <MapContainer center={[20.5937, 78.9629]} zoom={5} style={{ height: "100%", width: "100%" }}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap contributors'
            />
            
            {markers.map((marker) => (
                <Marker key={marker.id} position={[marker.lat, marker.lng]} icon={marker.icon}>
                    <Popup>
                        <div style={{textAlign:"center", minWidth:"150px"}}>
                            <strong style={{fontSize:"14px", color:"#333"}}>{marker.name}</strong>
                            <hr style={{margin:"5px 0", border:"0.5px solid #eee"}}/>
                            <div style={{fontSize:"13px", fontWeight:"bold", color: marker.type.includes("In") ? "#2980b9" : "#c0392b"}}>
                                {marker.type}
                            </div>
                            <div style={{fontSize:"12px", marginTop:"3px"}}>⏰ {marker.time}</div>
                            <div style={{fontSize:"11px", color:"gray", marginTop:"5px"}}>📍 {marker.address}</div>
                        </div>
                    </Popup>
                </Marker>
            ))}
          </MapContainer>

          {/* LEGEND BOX */}
          <div style={legendStyle}>
              <h4 style={{margin:"0 0 5px 0", fontSize:"12px"}}>Legend</h4>
              <div style={{display:"flex", alignItems:"center", marginBottom:"3px", fontSize:"11px"}}>
                  <img src="https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png" width="12" style={{marginRight:5}} alt="blue"/>
                  Punch In
              </div>
              <div style={{display:"flex", alignItems:"center", marginBottom:"3px", fontSize:"11px"}}>
                  <img src="https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png" width="12" style={{marginRight:5}} alt="red"/>
                  Punch Out / Visits
              </div>
              <div style={{display:"flex", alignItems:"center", fontSize:"11px"}}>
                  <img src="https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-violet.png" width="12" style={{marginRight:5}} alt="purple"/>
                  Auto Track (30m)
              </div>
              <hr style={{margin:"5px 0"}}/>
              <small>Points: <strong>{markers.length}</strong></small>
          </div>
      </div>
    </div>
  );
}

// STYLES
const headerStyle={padding:"10px 20px", background:"white", boxShadow:"0 2px 5px rgba(0,0,0,0.1)", display:"flex", justifyContent:"space-between", alignItems:"center", zIndex:1000};
const inputStyle={padding:"5px", borderRadius:"5px", border:"1px solid #ccc", fontSize:"13px"};
const btnRefresh={padding:"6px 12px", backgroundColor:"#27ae60", color:"white", border:"none", borderRadius:"5px", cursor:"pointer", fontWeight:"bold", fontSize:"13px"};
const loaderOverlay={position:"absolute", top:0, left:0, width:"100%", height:"100%", background:"rgba(255,255,255,0.7)", zIndex:999, display:"flex", justifyContent:"center", alignItems:"center"};
const legendStyle={position:"absolute", bottom:"20px", left:"20px", background:"white", padding:"10px", borderRadius:"8px", zIndex:1000, boxShadow:"0 0 10px rgba(0,0,0,0.3)", minWidth:"130px"};

export default MapView;